import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/src/documentNode';
import * as Apollo from '@apollo/client';
export const RefetchWorkspaceGuestsOnRemoveDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"RefetchWorkspaceGuestsOnRemove"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"workspaceId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"organization"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"workspaceId"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"collaborators"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"activityBlocked"}},{"kind":"Field","name":{"kind":"Name","value":"fullName"}},{"kind":"Field","name":{"kind":"Name","value":"nonPublic"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"avatarUrl"}},{"kind":"Field","name":{"kind":"Name","value":"fullName"}},{"kind":"Field","name":{"kind":"Name","value":"initials"}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"members"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"activityBlocked"}},{"kind":"Field","name":{"kind":"Name","value":"fullName"}},{"kind":"Field","name":{"kind":"Name","value":"nonPublic"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"avatarUrl"}},{"kind":"Field","name":{"kind":"Name","value":"fullName"}},{"kind":"Field","name":{"kind":"Name","value":"initials"}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"memberships"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"deactivated"}},{"kind":"Field","name":{"kind":"Name","value":"idMember"}},{"kind":"Field","name":{"kind":"Name","value":"memberType"}},{"kind":"Field","name":{"kind":"Name","value":"unconfirmed"}}]}},{"kind":"Field","name":{"kind":"Name","value":"offering"}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"RefetchWorkspaceGuestsOnRemove","document":RefetchWorkspaceGuestsOnRemoveDocument}} as const;
export type RefetchWorkspaceGuestsOnRemoveQueryVariables = Types.Exact<{
  workspaceId: Types.Scalars['ID']['input'];
}>;


export type RefetchWorkspaceGuestsOnRemoveQuery = (
  { __typename: 'Query' }
  & { organization?: Types.Maybe<(
    { __typename: 'Organization' }
    & Pick<Types.Organization, 'id' | 'offering'>
    & { collaborators: Array<(
      { __typename: 'Collaborator' }
      & Pick<Types.Collaborator, 'id' | 'activityBlocked' | 'fullName'>
      & { nonPublic?: Types.Maybe<(
        { __typename: 'Member_NonPublic' }
        & Pick<Types.Member_NonPublic, 'avatarUrl' | 'fullName' | 'initials'>
      )> }
    )>, members: Array<(
      { __typename: 'Member' }
      & Pick<Types.Member, 'id' | 'activityBlocked' | 'fullName'>
      & { nonPublic?: Types.Maybe<(
        { __typename: 'Member_NonPublic' }
        & Pick<Types.Member_NonPublic, 'avatarUrl' | 'fullName' | 'initials'>
      )> }
    )>, memberships: Array<(
      { __typename: 'Organization_Membership' }
      & Pick<Types.Organization_Membership, 'id' | 'deactivated' | 'idMember' | 'memberType' | 'unconfirmed'>
    )> }
  )> }
);

/**
 * __useRefetchWorkspaceGuestsOnRemoveQuery__
 *
 * To run a query within a React component, call `useRefetchWorkspaceGuestsOnRemoveQuery` and pass it any options that fit your needs.
 * When your component renders, `useRefetchWorkspaceGuestsOnRemoveQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useRefetchWorkspaceGuestsOnRemoveQuery({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *   },
 * });
 */
export function useRefetchWorkspaceGuestsOnRemoveQuery(
  baseOptions: TrelloQueryHookOptions<
    RefetchWorkspaceGuestsOnRemoveQuery,
    RefetchWorkspaceGuestsOnRemoveQueryVariables
  > &
    (
      | {
          variables: RefetchWorkspaceGuestsOnRemoveQueryVariables;
          skip?: boolean;
        }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: RefetchWorkspaceGuestsOnRemoveDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    RefetchWorkspaceGuestsOnRemoveQuery,
    RefetchWorkspaceGuestsOnRemoveQueryVariables
  >(RefetchWorkspaceGuestsOnRemoveDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useRefetchWorkspaceGuestsOnRemoveLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    RefetchWorkspaceGuestsOnRemoveQuery,
    RefetchWorkspaceGuestsOnRemoveQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    RefetchWorkspaceGuestsOnRemoveQuery,
    RefetchWorkspaceGuestsOnRemoveQueryVariables
  >(RefetchWorkspaceGuestsOnRemoveDocument, options);
}
export function useRefetchWorkspaceGuestsOnRemoveSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        RefetchWorkspaceGuestsOnRemoveQuery,
        RefetchWorkspaceGuestsOnRemoveQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    RefetchWorkspaceGuestsOnRemoveQuery,
    RefetchWorkspaceGuestsOnRemoveQueryVariables
  >(RefetchWorkspaceGuestsOnRemoveDocument, options);
}
export type RefetchWorkspaceGuestsOnRemoveQueryHookResult = ReturnType<
  typeof useRefetchWorkspaceGuestsOnRemoveQuery
>;
export type RefetchWorkspaceGuestsOnRemoveLazyQueryHookResult = ReturnType<
  typeof useRefetchWorkspaceGuestsOnRemoveLazyQuery
>;
export type RefetchWorkspaceGuestsOnRemoveSuspenseQueryHookResult = ReturnType<
  typeof useRefetchWorkspaceGuestsOnRemoveSuspenseQuery
>;
export type RefetchWorkspaceGuestsOnRemoveQueryResult = Apollo.QueryResult<
  RefetchWorkspaceGuestsOnRemoveQuery,
  RefetchWorkspaceGuestsOnRemoveQueryVariables
>;
